/* eslint-disable react/jsx-props-no-spreading */
import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useFormik } from 'formik';
import InputMask from 'react-input-mask';
import { object, string } from 'yup';
import { ERRORS, URL } from 'appConstants';
import { _t, lockBodyScroll } from 'utils';
import { apiClient } from 'utils/api';

import iconClip from 'assets/img/vacancies-v4/icon-clip.svg';
import iconSend from 'assets/img/vacancies-v4/icon-send.svg';

import styles from './styles.module.scss';

type Props = {
  onClose: () => void;
  vacancyTitle?: string;
};

type FormValues = {
  fullName: string;
  email: string;
  phone: string;
  cover: string;
};

const initialValues: FormValues = {
  fullName: '',
  email: '',
  phone: '',
  cover: '',
};

const validationSchema = object().shape({
  fullName: string().required(ERRORS.required),
  email: string().email(ERRORS.email).required(ERRORS.required),
  phone: string().min(18, ERRORS.phone).required(ERRORS.required),
  cover: string(),
});

const getErrorMessage = (err: unknown) => {
  const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
  if (!data || typeof data !== 'object') return '';
  if (typeof data.detail === 'string') return data.detail;
  const first = Object.values(data).find((value) => (
    Array.isArray(value) && typeof value[0] === 'string'
  ));
  if (Array.isArray(first) && typeof first[0] === 'string') return first[0];
  return '';
};

const ResumeForm: FC<Props> = ({ onClose, vacancyTitle = '' }) => {
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => lockBodyScroll(), []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    setFieldValue,
  } = useFormik<FormValues>({
    initialValues,
    validationSchema,
    onSubmit: async (form) => {
      if (!file) {
        setFileError(ERRORS.required);
        return;
      }
      setSubmitting(true);
      setSubmitError('');
      try {
        const data = new FormData();
        data.append('full_name', form.fullName);
        data.append('email', form.email);
        data.append('phone', form.phone);
        data.append('cover', form.cover || '');
        if (vacancyTitle) data.append('vacancy_title', vacancyTitle);
        data.append('file', file);
        await apiClient.post(URL.VACANCY_RESUME, data);
        setSent(true);
      } catch (err) {
        setSubmitError(getErrorMessage(err) || 'Ошибка отправки резюме');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handlePhoneChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setFieldValue('phone', e.target.value);
  }, [setFieldValue]);

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.files && e.target.files[0];
    if (!next) {
      setFile(null);
      return;
    }
    const lower = next.name.toLowerCase();
    if (!lower.endsWith('.pdf') && !lower.endsWith('.docx') && !lower.endsWith('.doc')) {
      setFileError('Допустимы файлы DOCX и PDF');
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setFileError('');
    setFile(next);
  }, []);

  const fieldError = (name: keyof FormValues) => (
    errors[name] && touched[name] ? (
      <span className={styles.error}>{_t(String(errors[name]))}</span>
    ) : null
  );

  const renderLabel = (text: string, required?: boolean) => (
    <span className={styles.label}>
      {_t(text)}
      {required && <span className={styles.req}>*</span>}
    </span>
  );

  return createPortal(
    <div className={styles.root} role="dialog" aria-modal="true" aria-label={_t('Отправить резюме')}>
      <div className={styles.backdrop} />
      <div className={styles.scroll} onClick={onClose} role="presentation">
        <div className={styles.card} onClick={(e) => e.stopPropagation()}>
          <div className={styles.head}>
            <h2 className={styles.title}>{_t('Отправить резюме')}</h2>
            <button
              type="button"
              className={styles.close}
              aria-label={_t('Закрыть')}
              onClick={onClose}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M1 1l14 14M15 1L1 15" stroke="currentColor" strokeWidth="2" />
              </svg>
            </button>
          </div>

          {sent ? (
            <p className={styles.success}>
              {_t('Резюме успешно отправлено. Мы свяжемся с вами по указанным контактам.')}
            </p>
          ) : (
            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <div className={styles.field}>
                {renderLabel('ФИО', true)}
                <input
                  className={styles.control}
                  name="fullName"
                  value={values.fullName}
                  placeholder={_t('Иванов Иван Иванович')}
                  onChange={handleChange}
                  disabled={submitting}
                />
                {fieldError('fullName')}
              </div>

              <div className={styles.field}>
                {renderLabel('E-mail', true)}
                <input
                  className={styles.control}
                  name="email"
                  type="email"
                  value={values.email}
                  placeholder={_t('email@mail.ru')}
                  onChange={handleChange}
                  disabled={submitting}
                />
                {fieldError('email')}
              </div>

              <div className={styles.field}>
                {renderLabel('Телефон', true)}
                <InputMask
                  mask="+7 (999) 999-99-99"
                  value={values.phone}
                  onChange={handlePhoneChange}
                  disabled={submitting}
                >
                  {(inputProps: object) => (
                    <input
                      {...inputProps}
                      className={styles.control}
                      name="phone"
                      placeholder={_t('+7 (999) 000-00-00')}
                    />
                  )}
                </InputMask>
                {fieldError('phone')}
              </div>

              <div className={styles.field}>
                {renderLabel('Краткое сопроводительное')}
                <textarea
                  className={styles.textarea}
                  name="cover"
                  value={values.cover}
                  placeholder={_t('Напишите текст здесь')}
                  onChange={handleChange}
                  rows={4}
                  disabled={submitting}
                />
              </div>

              <div className={styles.fileField}>
                <span className={styles.label}>{_t('Файл резюме')}</span>
                <div className={styles.fileRow}>
                  <span className={styles.fileName}>
                    {_t(file ? file.name : 'Файл не выбран')}
                  </span>
                  <div className={styles.fileActions}>
                    <button
                      type="button"
                      className={styles.fileBtnIcon}
                      aria-label={_t('Прикрепить файл')}
                      onClick={() => fileRef.current && fileRef.current.click()}
                      disabled={submitting}
                    >
                      <img src={iconClip} alt="" width={7} height={12} />
                    </button>
                    <button
                      type="button"
                      className={styles.fileBtn}
                      onClick={() => fileRef.current && fileRef.current.click()}
                      disabled={submitting}
                    >
                      {_t('Прикрепить файл (DOCX, PDF)')}
                    </button>
                  </div>
                  <input
                    ref={fileRef}
                    className={styles.fileInput}
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleFileChange}
                    disabled={submitting}
                  />
                </div>
                {fileError ? <span className={styles.error}>{_t(fileError)}</span> : null}
              </div>

              {submitError ? <span className={styles.error}>{_t(submitError)}</span> : null}

              <button type="submit" className={styles.submit} disabled={submitting}>
                <span className={styles.submitLabel}>
                  {_t(submitting ? 'Отправка…' : 'Отправить')}
                </span>
                <span className={styles.submitIcon} aria-hidden="true">
                  <img src={iconSend} alt="" width={16} height={16} />
                </span>
              </button>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ResumeForm;
